import { useCallback, useEffect, useRef, useState } from 'react';
import { MicVAD } from '@ricky0123/vad-web';
import * as ort from 'onnxruntime-web';

// Filter ONNX warnings
const originalWarn = console.warn;
console.warn = (...args: any[]) => {
  const message = args[0]?.toString() || '';
  if (!message.includes('[W:onnxruntime:, graph.cc:3490 CleanUnusedInitializersAndNodeArgs]')) {
    originalWarn.apply(console, args);
  }
};

interface UseSpeechDetectionProps {
  onSpeechStart?: () => void;
  onSpeechEnd?: () => void;
  onError?: (error: string) => void;
  enabled?: boolean;
}

export const useSpeechDetection = ({
  onSpeechStart,
  onSpeechEnd,
  onError,
  enabled = true
}: UseSpeechDetectionProps) => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const enabledRef = useRef(enabled);
  const vadRef = useRef<MicVAD | null>(null);
  const speechEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSpeakingRef = useRef(false);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const cleanup = useCallback(async () => {
    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
      speechEndTimeoutRef.current = null;
    }

    if (vadRef.current) {
      try {
        if (typeof vadRef.current.stop === 'function') {
          await vadRef.current.stop();
        } else {
          if (vadRef.current.destroy && typeof vadRef.current.destroy === 'function') {
            await vadRef.current.destroy();
          }
          if (vadRef.current.close && typeof vadRef.current.close === 'function') {
            await vadRef.current.close();
          }
        }
      } catch (err) {
        console.warn('Non-critical error during VAD cleanup:', err);
      } finally {
        vadRef.current = null;
      }
    }
    
    if (stream) {
      try {
        const tracks = stream.getTracks();
        tracks.forEach(track => {
          try {
            track.stop();
          } catch (err) {
            console.warn('Error stopping track:', err);
          }
        });
      } catch (err) {
        console.warn('Error stopping media stream:', err);
      }
      setStream(null);
    }
    
    setIsListening(false);
    setError(null);
    isSpeakingRef.current = false;
  }, [stream]);

  const startListening = useCallback(async () => {
    if (isInitializing || vadRef.current || !enabledRef.current) {
      return;
    }

    setIsInitializing(true);
    setError(null);

    try {
      await cleanup();

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      if (!enabledRef.current) {
        try {
          mediaStream.getTracks().forEach(track => track.stop());
        } catch (err) {
          console.warn('Error stopping media stream:', err);
        }
        setIsInitializing(false);
        return;
      }

      setStream(mediaStream);

      const vad = await MicVAD.new({
        stream: mediaStream,
        onSpeechStart: () => {
          if (!enabledRef.current) return;
          console.log('Speech detected');
          
          if (speechEndTimeoutRef.current) {
            clearTimeout(speechEndTimeoutRef.current);
            speechEndTimeoutRef.current = null;
          }
          
          if (!isSpeakingRef.current) {
            isSpeakingRef.current = true;
            setIsListening(true);
            onSpeechStart?.();
          }
        },
        onSpeechEnd: () => {
          if (!enabledRef.current || !isSpeakingRef.current) return;
          
          if (speechEndTimeoutRef.current) {
            clearTimeout(speechEndTimeoutRef.current);
          }
          
          speechEndTimeoutRef.current = setTimeout(() => {
            if (isSpeakingRef.current) {
              isSpeakingRef.current = false;
              setIsListening(false);
              onSpeechEnd?.();
            }
          }, 1000);
        },
        onError: (err: Error) => {
          console.error('VAD error:', err);
          setError(err);
          onError?.(err);
        }
      });

      if (!enabledRef.current) {
        try {
          if (typeof vad.stop === 'function') {
            await vad.stop();
          }
          mediaStream.getTracks().forEach(track => track.stop());
        } catch (err) {
          console.warn('Error during cleanup:', err);
        }
        setIsInitializing(false);
        return;
      }

      vadRef.current = vad;
      try {
        await vad.start();
      } catch (err) {
        console.error('Error starting VAD:', err);
        cleanup();
      }

    } catch (err) {
      console.error('Error initializing speech detection:', err);
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      onError?.(error);
      cleanup();
    } finally {
      setIsInitializing(false);
    }
  }, [cleanup, onSpeechStart, onSpeechEnd, onError]);

  const stopListening = useCallback(async () => {
    await cleanup();
  }, [cleanup]);

  useEffect(() => {
    if (enabled) {
      startListening();
    } else {
      stopListening();
    }
  }, [enabled, startListening, stopListening]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isListening,
    stream,
    error,
    isInitializing
  };
};
